import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import { errMsg } from "../../lib/portfolio";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Textarea } from "../../components/ui/textarea";

export default function DailyLogDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [l, setL] = useState(null);
  const [note, setNote] = useState("");
  const [error, setError] = useState(null);
  const load = useCallback(() => api.get(`/phase4/daily-logs/${id}`).then((r) => setL(r.data)).catch((e) => setError(errMsg(e, "Could not load."))), [id]);
  useEffect(() => { load(); }, [load]);
  const act = (p) => p.then(() => { setError(null); setNote(""); load(); }).catch((e) => setError(errMsg(e, "That did not work.")));
  const role = l?.role_key;
  const pmLike = ["OWNER_SUPER_ADMIN", "ADMINISTRATOR", "PROJECT_MANAGER"].includes(role);
  return (
    <AdminAppShell activeNav="field" user={user} title={l ? `Daily report · ${l.log_date}` : "Daily report"} subtitle={l ? `State: ${l.state}` : ""}
      breadcrumbs={[{ label: "Field Operations", to: "/phase4/field" }, { label: l?.log_date || "Report", to: null }]}>
      <div data-testid="phase4-log-detail">
        {error && <p role="alert" className="mb-3 text-sm text-[#991B1B]" data-testid="phase4-log-detail-error">{error}</p>}
        {l && (
          <Card className="ds-card border-erf-borderCream">
            <CardContent className="p-5 space-y-3 text-sm font-nav text-erf-dark">
              <h2 className="font-display font-semibold text-base md:text-lg">Conditions</h2>
              <p data-testid="phase4-log-weather-text">{l.weather_summary || "No weather entered"}{l.temperature_f != null ? ` · ${l.temperature_f}°F` : ""} (entered manually)</p>
              <h2 className="font-display font-semibold text-base md:text-lg">Crew</h2>
              <ul>{l.manpower.map((c, i) => <li key={i}>{c.trade}: {c.headcount} workers, {c.hours} h</li>)}{l.manpower.length === 0 && <li>No crew entered.</li>}</ul>
              <p><strong>Total:</strong> {l.total_headcount} workers · {l.total_hours} h</p>
              <h2 className="font-display font-semibold text-base md:text-lg">Work performed</h2>
              <p className="whitespace-pre-wrap" data-testid="phase4-log-work-text">{l.work_performed}</p>
              {l.delays && <p><strong>Delays:</strong> {l.delays}</p>}
              <div className="flex flex-wrap gap-2">
                {l.state === "draft" && role !== "PROJECT_MANAGER" && <Button className="h-11 bg-erf-gold text-erf-dark hover:bg-erf-goldHover" onClick={() => act(api.post(`/phase4/daily-logs/${id}/submit`))} data-testid="phase4-log-submit-btn">Submit report</Button>}
                {l.state === "submitted" && pmLike && <Button className="h-11 bg-erf-dark text-[#FEFCF7]" onClick={() => act(api.post(`/phase4/daily-logs/${id}/acknowledge`))} data-testid="phase4-log-ack-btn">Acknowledge</Button>}
              </div>
              {l.state === "acknowledged" && (
                <>
                  <h2 className="font-display font-semibold text-base md:text-lg">Amendments</h2>
                  <p className="text-xs text-erf-dark/80">Acknowledged reports are locked. Add a correction instead; it cannot be edited later.</p>
                  <ul>{l.amendments.map((a) => <li key={a.id} data-testid={`phase4-amendment-${a.id}`}>{new Date(a.created_at).toLocaleString()}: {a.note}</li>)}</ul>
                  <label className="block">Correction (at least 10 characters)
                    <Textarea value={note} onChange={(e) => setNote(e.target.value)} className="mt-1" data-testid="phase4-amend-text" />
                  </label>
                  <Button variant="outline" className="h-11 border-erf-borderCream" onClick={() => act(api.post(`/phase4/daily-logs/${id}/amendments`, { reason: note }))} data-testid="phase4-amend-btn">Add correction</Button>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </AdminAppShell>
  );
}
